import React, { Fragment, useRef, useState } from "react";
import Button from "components/Form/fields/Button";
import { Formik, useFormikContext } from "formik";
import { FORM_KEY } from "utils/constants/form";
import { BUTTON_TAGS, UI_THEME } from "utils/constants/ui";
import { SERVICE_LABEL_MAP } from "utils/constants/workflow";
import { useEligibleWorkflowIntegrations } from "utils/hooks/integration";
import PATHS from "utils/paths";
import { interpolateRoute } from "utils/route";
import StepEditor from "./StepEditor";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Image from "components/Image";
import { updateWorkflow, deleteWorkflow } from "actions/workflow";
import { STATE_KEYS } from "utils/constants/state";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import Padder from "components/Padder";
import {
  prepareEditWorkflowInitialValues,
  prepareUpdateWorkflow,
  scaffoldWorkflowStep,
  validateWorfklowSchema
} from "utils/workflow";
import FormActions from "components/FormActions";
import { useCtxReferences } from "utils/selectors";
import ConfirmIconButton from "components/ConfirmIconButton";
import { getWorkflowDeleteModalSlides } from "utils/constants/modal";
import { getUUID } from "utils/uuid";

const WorkflowEditor = () => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const eligibleIntegrations = useEligibleWorkflowIntegrations();
  const steps = get(values, STATE_KEYS.WORKFLOW.STEPS) || [];
  const stepsTouched = get(touched, STATE_KEYS.WORKFLOW.STEPS);
  let content;

  const hasSteps = steps.length;
  if (hasSteps) {
    const stepNodes = steps.map((step, stepIx) => {
      return (
        <li className={`w-100 ${stepIx ? "pt3" : ""}`} key={step.uuid}>
          <StepEditor
            name={`${STATE_KEYS.WORKFLOW.STEPS}[${stepIx}]`}
            uuid={step.uuid}
            index={stepIx}
          />
        </li>
      );
    });

    content = <ul className="list ma0 pa0">{stepNodes}</ul>;
  } else {
    const selectList = [];
    for (const service in eligibleIntegrations) {
      const integration = eligibleIntegrations[service][0];

      const actionProps = integration
        ? {
            copy: "Select",
            tag: BUTTON_TAGS.BUTTON,
            theme: UI_THEME.SLIM,
            onClick: () => {
              const updatedSteps = cloneDeep(values.data.steps);
              const newStep = scaffoldWorkflowStep(integration);
              updatedSteps.push(newStep);
              setFieldValue(STATE_KEYS.WORKFLOW.STEPS, updatedSteps);
              if (!stepsTouched) {
                setFieldTouched(STATE_KEYS.WORKFLOW.STEPS);
              }

              setFieldValue(`${FORM_KEY}.focusStepUuid`, newStep.uuid);
            }
          }
        : {
            copy: "Connect",
            tag: BUTTON_TAGS.LINK,
            theme: UI_THEME.SLIM,
            href: interpolateRoute(PATHS.OAUTH_INITIATE_SERVICE, {
              service
            })
          };

      const serviceLabel = SERVICE_LABEL_MAP[service];

      selectList.push(
        <li key={service}>
          <div className="pa3 w-100 flex flex-row items-center ba br2 hairline-1 bg-white">
            <Image
              src={`/assets/integrations/${service}/icon.svg`}
              width={40}
              height={40}
              alt={service}
            ></Image>
            <div className="ph2 flex flex-row w-100 justify-between items-center">
              <div className="f4 fw6 pl1">{serviceLabel}</div>
              <Button {...actionProps} />
            </div>
          </div>
        </li>
      );
    }

    content = <ul className="list ma0 pa0">{selectList}</ul>;
  }

  return <div className="w-100">{content}</div>;
};

const WorkflowEditorContainer = ({ workflow, variables, actions }) => {
  const formRef = useRef(null);
  const { resourceUUID, resource } = useCtxReferences();
  const [editorKey, setEditorKey] = useState(getUUID());

  const submitForm = (formValues, formikActions) => {
    const preparedData = prepareUpdateWorkflow(formValues);
    actions.updateWorkflow(
      {
        resourceUUID,
        resource,
        uuid: workflow.uuid,
        data: preparedData
      },
      (err, _) => {
        /**
         * 1. Turn off loader
         */
        formikActions.setSubmitting(false);
        /**
         * 2. Show errors or reset form
         */
        if (err) {
          formikActions.setErrors(err);
        } else {
          formikActions.resetForm({ values: formValues });
        }
      }
    );
  };

  const deleteWorkflow = (callback) => {
    actions.deleteWorkflow(
      {
        resourceUUID,
        resource,
        uuid: workflow.uuid
      },
      (err, response) => {
        if (!err) {
          // goBack();
        }
        callback(err, response);
      }
    );
  };

  const initialValues = prepareEditWorkflowInitialValues({
    workflow,
    variables
  });

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={submitForm}
      validate={validateWorfklowSchema}
    >
      {() => (
        <Fragment>
          <div className="w-100 bg-white bb hairline-1 ph4 sticky top-0 z-1">
            <FormActions
              theme={UI_THEME.SLIM}
              onReset={() => setEditorKey(getUUID())}
              customClasses={{
                container: "w-100 flex flex-row justify-end items-center pv3"
              }}
            >
              <div className="pr2">
                <ConfirmIconButton
                  slides={getWorkflowDeleteModalSlides()}
                  icon="delete"
                  onConfirm={deleteWorkflow}
                />
              </div>
            </FormActions>
          </div>
          <div className="mw8 center w-100 pt4">
            <WorkflowEditor key={editorKey} />
            <Padder theme="visible" />
            <Padder theme="visible" />
          </div>
        </Fragment>
      )}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      updateWorkflow,
      deleteWorkflow
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(WorkflowEditorContainer);
